export const DEFAULT_YEARS_BACK = 10
export const DEFAULT_MAX_RANGE_DAYS = 366

export function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export function daysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate()
}

export function buildDateFromParts(day: number, month: number, year: number): Date | null {
  if (month < 1 || month > 12 || day < 1) return null
  const maxDay = daysInMonth(month, year)
  if (day > maxDay) return null
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return startOfDay(date)
}

/** Parses DD/MM/YYYY with / - or . separators */
export function parseDdMmYyyy(value: string): Date | null {
  const trimmed = value.trim()
  const match = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (!match) return null

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  return buildDateFromParts(day, month, year)
}

export function formatDateDdMmYyyy(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0')
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const y = date.getFullYear()
  return `${d}/${m}/${y}`
}

export function addDays(date: Date, days: number): Date {
  const d = startOfDay(date)
  d.setDate(d.getDate() + days)
  return d
}

export type DatePartsValue = {
  day: number | null
  month: number | null
  year: number | null
}

export function isDatePartsComplete(parts: DatePartsValue): boolean {
  return parts.day != null && parts.month != null && parts.year != null
}

export function dateFromParts(parts: DatePartsValue): Date | null {
  if (!isDatePartsComplete(parts)) return null
  return buildDateFromParts(parts.day!, parts.month!, parts.year!)
}

export function getYearOptions(minDate: Date, maxDate: Date): number[] {
  const minYear = minDate.getFullYear()
  const maxYear = maxDate.getFullYear()
  const years: number[] = []
  for (let y = maxYear; y >= minYear; y -= 1) {
    years.push(y)
  }
  return years
}

export function getMonthOptions(year: number, minBound: Date, maxBound: Date): number[] {
  let from = 1
  let to = 12
  if (year === minBound.getFullYear()) {
    from = Math.max(from, minBound.getMonth() + 1)
  }
  if (year === maxBound.getFullYear()) {
    to = Math.min(to, maxBound.getMonth() + 1)
  }
  const months: number[] = []
  for (let m = from; m <= to; m += 1) {
    months.push(m)
  }
  return months
}

export function getDayOptions(
  year: number,
  month: number,
  minBound: Date,
  maxBound: Date
): number[] {
  let from = 1
  let to = daysInMonth(month, year)
  if (year === minBound.getFullYear() && month === minBound.getMonth() + 1) {
    from = Math.max(from, minBound.getDate())
  }
  if (year === maxBound.getFullYear() && month === maxBound.getMonth() + 1) {
    to = Math.min(to, maxBound.getDate())
  }
  const days: number[] = []
  for (let d = from; d <= to; d += 1) {
    days.push(d)
  }
  return days
}

/** Month list for partial selection (year optional). */
export function getMonthOptionsForParts(
  parts: DatePartsValue,
  minBound: Date,
  maxBound: Date
): number[] {
  if (parts.year != null) {
    return getMonthOptions(parts.year, minBound, maxBound)
  }
  const months: number[] = []
  for (let m = 1; m <= 12; m += 1) {
    months.push(m)
  }
  return months
}

/** Day list for partial selection (year/month optional until both set for bounds). */
export function getDayOptionsForParts(
  parts: DatePartsValue,
  minBound: Date,
  maxBound: Date
): number[] {
  if (parts.year != null && parts.month != null) {
    return getDayOptions(parts.year, parts.month, minBound, maxBound)
  }

  if (parts.month != null) {
    const refYear = maxBound.getFullYear()
    const to = daysInMonth(parts.month, refYear)
    const days: number[] = []
    for (let d = 1; d <= to; d += 1) {
      days.push(d)
    }
    return days
  }

  const days: number[] = []
  for (let d = 1; d <= 31; d += 1) {
    days.push(d)
  }
  return days
}

function pickNearestAllowed(value: number, allowed: number[]): number | null {
  if (allowed.length === 0) return null
  if (allowed.includes(value)) return value
  return allowed[allowed.length - 1] ?? null
}

/** Clamp each filled field without clearing other fields (any pick order: DD/MM/YYYY). */
export function clampDateParts(
  parts: DatePartsValue,
  minBound: Date,
  maxBound: Date
): DatePartsValue {
  let {day, month, year} = parts

  if (year != null) {
    const years = getYearOptions(minBound, maxBound)
    year = pickNearestAllowed(year, years)
  }

  if (month != null) {
    const months = getMonthOptionsForParts({day, month, year}, minBound, maxBound)
    month = pickNearestAllowed(month, months)
  }

  if (day != null) {
    const days = getDayOptionsForParts({day, month, year}, minBound, maxBound)
    day = pickNearestAllowed(day, days)
  }

  return {day, month, year}
}

export type DateRangeValidation = {
  isComplete: boolean
  start: Date | null
  end: Date | null
  isValid: boolean
  errorId: string | null
}

export function validateDateRangeSelection(params: {
  startParts: DatePartsValue
  endParts: DatePartsValue
  minDate: Date
  maxDate: Date
  maxRangeDays?: number
}): DateRangeValidation {
  const {startParts, endParts, minDate, maxDate, maxRangeDays} = params
  const incomplete = {
    isComplete: false,
    start: null,
    end: null,
    isValid: false,
    errorId: 'CHATBOT.ATTENDANCE.DATE_RANGE_INCOMPLETE',
  }

  if (!isDatePartsComplete(startParts) || !isDatePartsComplete(endParts)) {
    return incomplete
  }

  const start = dateFromParts(startParts)
  const end = dateFromParts(endParts)

  if (!start || !end) {
    return {
      isComplete: true,
      start,
      end,
      isValid: false,
      errorId: 'CHATBOT.ATTENDANCE.INVALID_DATE',
    }
  }

  if (start > maxDate || end > maxDate) {
    return {
      isComplete: true,
      start,
      end,
      isValid: false,
      errorId: 'CHATBOT.ATTENDANCE.FUTURE_DATE',
    }
  }

  if (start < minDate || end < minDate) {
    return {
      isComplete: true,
      start,
      end,
      isValid: false,
      errorId: 'CHATBOT.ATTENDANCE.DATE_RANGE_TOO_OLD',
    }
  }

  if (end < start) {
    return {
      isComplete: true,
      start,
      end,
      isValid: false,
      errorId: 'CHATBOT.ATTENDANCE.INVALID_RANGE',
    }
  }

  if (maxRangeDays != null && maxRangeDays > 0) {
    const maxEnd = addDays(start, maxRangeDays)
    const cappedMaxEnd = maxEnd > maxDate ? maxDate : maxEnd
    if (end > cappedMaxEnd) {
      return {
        isComplete: true,
        start,
        end,
        isValid: false,
        errorId: 'CHATBOT.ATTENDANCE.DATE_RANGE_TOO_LONG',
      }
    }
  }

  return {
    isComplete: true,
    start,
    end,
    isValid: true,
    errorId: null,
  }
}
